import type { AnalyticsProperties } from './analytics';

type Send = (properties: AnalyticsProperties, flushImmediately: boolean) => void;
type Pending = { properties: AnalyticsProperties; send: Send; immediate: boolean };
type Visit = { paymentId: string; abtest?: string; methodAbtest?: string; pending: Pending[]; owner?: symbol };
let visit: Visit | undefined;
let listening = false;

export const paymentIdFromPath = (path: string) => {
    const match = /^\/payment\/([^/]+)\/?$/.exec(path);
    return match ? match[1] : undefined;
};

function drain(current: Visit, abtest: string, immediate = false) {
    const pending = current.pending.splice(0);
    pending.forEach(item => item.send({
        ...item.properties,
        abtest,
        ...(current.methodAbtest ? { method_abtest: current.methodAbtest } : {}),
    }, immediate || item.immediate));
}

function currentVisit(paymentId: string) {
    if (visit?.paymentId !== paymentId) {
        if (visit) drain(visit, visit.abtest || 'unknown', true);
        visit = { paymentId, pending: [] };
    }
    if (!listening && typeof window !== 'undefined') {
        listening = true;
        window.addEventListener('pagehide', () => { if (visit) drain(visit, visit.abtest || 'unknown', true); });
    }
    return visit!;
}

export function resolvePaymentAnalytics(paymentId: string, abtest: string, methodAbtest = 'none') {
    // A late response from a previous route must never overwrite the active visit.
    if (typeof window !== 'undefined') {
        const routeId = paymentIdFromPath(window.location.pathname);
        if (routeId && routeId !== paymentId) return;
    }
    const current = currentVisit(paymentId);
    current.abtest = abtest;
    current.methodAbtest = methodAbtest;
    drain(current, abtest);
}

export function finishPaymentAnalytics(paymentId: string) {
    if (visit?.paymentId !== paymentId) return;
    drain(visit, visit.abtest || 'unknown', true);
    visit = undefined;
}

export function retainPaymentAnalytics(paymentId: string) {
    const current = currentVisit(paymentId);
    const owner = Symbol(paymentId);
    current.owner = owner;
    return () => { queueMicrotask(() => {
        // StrictMode effect replay or a new visit supersedes the old cleanup.
        if (visit === current && current.owner === owner) finishPaymentAnalytics(paymentId);
    }); };
}

/** Every payment event, including route/screen views, passes through this gate. */
export function dispatchPaymentAnalytics(properties: AnalyticsProperties, send: Send, immediate = false) {
    const routeId = paymentIdFromPath(String(properties.page_path || ''));
    const paymentId = typeof properties.payment_id === 'string' ? properties.payment_id : routeId;
    if (!routeId) {
        if (visit) { drain(visit, visit.abtest || 'unknown', true); visit = undefined; }
        send(properties, immediate);
        return;
    }
    if (paymentId !== routeId) {
        send({ ...properties, abtest: properties.abtest || 'unknown' }, immediate);
        return;
    }
    const current = currentVisit(routeId);
    const enriched = { ...properties, payment_id: routeId, occurred_at: new Date().toISOString(), time: Date.now() / 1000 };
    if (current.abtest) send({
        ...enriched,
        abtest: current.abtest,
        method_abtest: current.methodAbtest ?? properties.method_abtest ?? 'none',
    }, immediate);
    else if (immediate) {
        drain(current, 'unknown', true);
        send({ ...enriched, abtest: 'unknown' }, true);
    } else {
        // Bound memory even if a network request stalls indefinitely.
        if (current.pending.length >= 100) drain(current, 'unknown');
        current.pending.push({ properties: enriched, send, immediate });
    }
}
